import { defaultRangeExtractor, useVirtualizer } from "@tanstack/react-virtual";
import {
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const NO_PINNED_KEYS: string[] = [];

const getTabStops = (row: HTMLElement) => {
  const controls = Array.from(
    row.querySelectorAll<HTMLElement>(
      "button, a[href], input, select, textarea, [tabindex]",
    ),
  ).filter(
    (element) =>
      element.tabIndex >= 0 &&
      !element.matches(':disabled, [aria-disabled="true"]') &&
      element.getClientRects().length > 0,
  );
  return controls.filter((element) => {
    if (!(element instanceof HTMLInputElement) || element.type !== "radio")
      return true;
    if (!element.name) return true;
    const group = controls.filter(
      (control): control is HTMLInputElement =>
        control instanceof HTMLInputElement &&
        control.type === "radio" &&
        control.name === element.name &&
        control.form === element.form,
    );
    return element === (group.find((radio) => radio.checked) ?? group[0]);
  });
};

/** Shares the modal's scrollbar, measuring each question as its content changes. */
export function VirtualSurveyQuestionList<T>({
  items,
  getKey,
  scrollElement,
  renderItem,
  estimateSize = 120,
  rowClassName,
  label = "Survey questions",
  revealRequest,
  pinnedKeys = NO_PINNED_KEYS,
}: {
  items: T[];
  getKey: (item: T) => string;
  scrollElement: HTMLDivElement | null;
  renderItem: (item: T, index: number) => ReactNode;
  estimateSize?: number;
  rowClassName?: string;
  label?: string;
  revealRequest?: { key: string };
  pinnedKeys?: string[];
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const tabEntryFromEnd = useRef<boolean | null>(null);
  const [scrollMargin, setScrollMargin] = useState(0);
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const [pendingFocus, setPendingFocus] = useState<{
    key: string;
    last: boolean;
  } | null>(null);
  const indexes = useMemo(
    () => new Map(items.map((item, index) => [getKey(item), index])),
    [getKey, items],
  );
  const getItemKey = useCallback(
    (index: number) => getKey(items[index]),
    [getKey, items],
  );
  const rangeExtractor = useCallback(
    (range: Parameters<typeof defaultRangeExtractor>[0]) => {
      const visible = new Set(defaultRangeExtractor(range));
      // Keep active inputs (including their open popovers) mounted off screen.
      for (const key of [focusedKey, pendingFocus?.key, ...pinnedKeys]) {
        const index = key ? indexes.get(key) : undefined;
        if (index !== undefined) visible.add(index);
      }
      return Array.from(visible).sort((left, right) => left - right);
    },
    [focusedKey, indexes, pendingFocus?.key, pinnedKeys],
  );
  const virtualizer = useVirtualizer({
    count: items.length,
    getItemKey,
    getScrollElement: () => scrollElement,
    initialOffset: () => scrollElement?.scrollTop ?? 0,
    estimateSize: () => estimateSize,
    overscan: 5,
    scrollMargin,
    rangeExtractor,
    enabled: Boolean(scrollElement),
  });
  const virtualItems = virtualizer.getVirtualItems();
  const updateScrollMargin = useCallback(() => {
    if (!listRef.current || !scrollElement) return;
    const offset = Math.max(
      0,
      Math.round(
        listRef.current.getBoundingClientRect().top -
          scrollElement.getBoundingClientRect().top +
          scrollElement.scrollTop -
          scrollElement.clientTop,
      ),
    );
    setScrollMargin((current) => (current === offset ? current : offset));
  }, [scrollElement]);

  // Entry from the header/footer must reach the logical first/last question,
  // even when the scrollbar currently shows a different part of the survey.
  useLayoutEffect(() => {
    const dialog = scrollElement?.closest('[role="dialog"]');
    if (!dialog) return;
    const recordTab = (event: Event) => {
      tabEntryFromEnd.current =
        event instanceof window.KeyboardEvent &&
        event.key === "Tab" &&
        !listRef.current?.contains(event.target as Node)
          ? event.shiftKey
          : null;
    };
    const clearTab = () => {
      tabEntryFromEnd.current = null;
    };
    dialog.addEventListener("keydown", recordTab, true);
    dialog.addEventListener("pointerdown", clearTab, true);
    return () => {
      dialog.removeEventListener("keydown", recordTab, true);
      dialog.removeEventListener("pointerdown", clearTab, true);
    };
  }, [scrollElement]);

  useLayoutEffect(updateScrollMargin);
  useLayoutEffect(() => {
    if (!scrollElement || !listRef.current) return;
    const observer = new ResizeObserver(updateScrollMargin);
    observer.observe(listRef.current);
    observer.observe(scrollElement.firstElementChild ?? scrollElement);
    return () => {
      observer.disconnect();
    };
  }, [scrollElement, updateScrollMargin]);

  useLayoutEffect(() => {
    if (!revealRequest) return;
    setPendingFocus({ key: revealRequest.key, last: false });
  }, [revealRequest]);

  useLayoutEffect(() => {
    if (!pendingFocus || !scrollElement) return;
    const index = indexes.get(pendingFocus.key);
    if (index === undefined) {
      setPendingFocus(null);
      return;
    }
    virtualizer.scrollToIndex(index, { align: "auto" });
    const row = listRef.current?.querySelector<HTMLElement>(
      `[data-index="${index}"]`,
    );
    if (!row) return;
    const stops = getTabStops(row);
    const target = pendingFocus.last ? stops.at(-1) : stops[0];
    target?.focus();
    setPendingFocus(null);
  }, [indexes, pendingFocus, scrollElement, virtualItems, virtualizer]);

  const handleKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    index: number,
  ) => {
    if (
      event.key !== "Tab" ||
      event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      !event.currentTarget.contains(event.target as Node)
    )
      return;
    const stops = getTabStops(event.currentTarget);
    if (event.target !== (event.shiftKey ? stops[0] : stops.at(-1))) return;
    const nextIndex = index + (event.shiftKey ? -1 : 1);
    if (nextIndex < 0 || nextIndex >= items.length) return;
    event.preventDefault();
    setPendingFocus({ key: getKey(items[nextIndex]), last: event.shiftKey });
  };

  return (
    <div
      ref={listRef}
      role="list"
      aria-label={label}
      className="relative"
      style={{ height: virtualizer.getTotalSize(), overflowAnchor: "none" }}
    >
      {virtualItems.map((item) => (
        <div
          key={item.key}
          ref={virtualizer.measureElement}
          data-index={item.index}
          role="listitem"
          aria-posinset={item.index + 1}
          aria-setsize={items.length}
          className="absolute top-0 left-0 w-full"
          style={{ transform: `translateY(${item.start - scrollMargin}px)` }}
          onFocusCapture={(event) => {
            setFocusedKey(getKey(items[item.index]));
            const last = tabEntryFromEnd.current;
            tabEntryFromEnd.current = null;
            if (
              last !== null &&
              !listRef.current?.contains(event.relatedTarget as Node | null)
            ) {
              const index = last ? items.length - 1 : 0;
              if (index !== item.index)
                setPendingFocus({ key: getKey(items[index]), last });
            }
          }}
          onBlurCapture={(event) => {
            if (
              !event.currentTarget.contains(event.relatedTarget as Node | null)
            )
              setFocusedKey(null);
          }}
          onKeyDown={(event) => {
            handleKeyDown(event, item.index);
          }}
        >
          <div className={rowClassName}>
            {renderItem(items[item.index], item.index)}
          </div>
        </div>
      ))}
    </div>
  );
}
